import { inject } from '@angular/core';
import { Router, CanActivateFn, UrlTree } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { filter, timeout } from 'rxjs/operators';
import { AccountState } from 'src/app/store/account/account.state';

/**
 * Waits for AccountState to be fully initialized before the guard reads any
 * signal values. Mirrors the pattern used by the other async account guards.
 */
async function waitForAccountInitialized(accountState: AccountState): Promise<void> {
  await firstValueFrom(
    toObservable(accountState.initialized).pipe(
      filter(initialized => initialized === true),
      timeout(10000),
    ),
  );
}

/**
 * Data Opt-In Guard
 *
 * Blocks the data-handling surfaces (notebook, admin) until the user has
 * accepted the current terms-of-service / privacy-policy version. When the
 * user has not opted in, they are redirected to `/my-blume`, where the
 * consent UI is rendered in place of the navigation tiles.
 *
 * `/my-blume` itself is intentionally NOT protected by this guard so the
 * acceptance surface stays reachable.
 *
 * Runs straight after `emailVerifiedGuard` and BEFORE `profileGuard`: terms
 * come first, then "Complete your profile". It returns a UrlTree rather than
 * navigating itself, so Angular's ordered guard results decide the redirect
 * deterministically when several guards would redirect at once.
 *
 * @example
 * // In app.routes.ts
 * {
 *   path: 'notebook',
 *   canActivate: [authGuard, emailVerifiedGuard, dataOptInGuard, profileGuard, licenseGuardFor('tools')],
 *   loadComponent: () => import('./features/notebook/notebook.page')
 * }
 *
 * @returns true if the current terms are accepted, otherwise a UrlTree to `/my-blume`
 */
export const dataOptInGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const accountState = inject(AccountState);
  const router = inject(Router);

  try {
    await waitForAccountInitialized(accountState);

    return accountState.hasAcceptedCurrentTerms() ? true : router.createUrlTree(['/my-blume']);
  } catch (error) {
    console.error('[DataOptInGuard] Error checking account:', error);
    return router.createUrlTree(['/my-blume']);
  }
};
